import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Search, Users, X } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface Client {
  id: string;
  name: string;
  contactPhone: string | null;
  contactEmail: string | null;
  notes: string | null;
  primaryOperatorUserId: string | null;
  createdAt: string;
}

/**
 * Phone is shown masked except for ADMIN. Format: keep up to 4 leading
 * chars (country / operator prefix) and last 4, dot the middle. So
 * "+919876543210" becomes "+9198••••3210".
 */
function maskPhone(p: string | null | undefined): string {
  if (!p) return '—';
  const digits = p.replace(/[^\d+]/g, '');
  if (digits.length <= 6) return digits;
  return `${digits.slice(0, Math.min(5, digits.length - 4))}••••${digits.slice(-4)}`;
}

/**
 * Email mask: first char of local part + dots + @domain. Lets
 * accountants spot duplicates without leaking the contact.
 */
function maskEmail(e: string | null | undefined): string {
  if (!e) return '—';
  const at = e.indexOf('@');
  if (at <= 0) return '—';
  const local = e.slice(0, at);
  const domain = e.slice(at);
  const head = local.slice(0, 1);
  return `${head}${'•'.repeat(Math.max(3, local.length - 1))}${domain}`;
}

export function ClientsPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Client | null>(null);

  const list = useQuery<Client[]>({
    queryKey: ['clients', { q: search }],
    queryFn: () => {
      const p = new URLSearchParams();
      if (search) p.set('q', search);
      return api(`/api/clients?${p.toString()}`);
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Users className="h-5 w-5" />
        <h1 className="text-xl font-semibold">Clients</h1>
        <span className="text-sm text-muted-foreground">
          {list.data?.length ?? 0} {list.data?.length === 1 ? 'client' : 'clients'}
        </span>
        <div className="ml-auto" />
        <Button onClick={() => { setEditing(null); setShowForm(true); }} size="sm">
          <Plus className="mr-1 h-3.5 w-3.5" /> New client
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, phone, email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
          />
        </div>
      </div>

      {showForm ? (
        <ClientForm
          initial={editing}
          isAdmin={isAdmin}
          onCancel={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => {
            setShowForm(false);
            setEditing(null);
            qc.invalidateQueries({ queryKey: ['clients'] });
          }}
        />
      ) : null}

      {list.isLoading ? (
        <SkeletonRows />
      ) : (list.data?.length ?? 0) === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-sm text-muted-foreground">No clients yet.</p>
            <Button className="mt-3" onClick={() => { setEditing(null); setShowForm(true); }}>
              <Plus className="mr-1 h-3.5 w-3.5" /> Add your first client
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <ul className="divide-y">
              {list.data!.map((c) => (
                <ClientRow
                  key={c.id}
                  c={c}
                  isAdmin={isAdmin}
                  onEdit={() => { setEditing(c); setShowForm(true); }}
                />
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function ClientRow({
  c,
  isAdmin,
  onEdit,
}: {
  c: Client;
  isAdmin: boolean;
  onEdit: () => void;
}) {
  const phone = isAdmin ? c.contactPhone : maskPhone(c.contactPhone);
  const email = isAdmin ? c.contactEmail : maskEmail(c.contactEmail);
  const sub = [phone, email].filter((v) => v && v !== '—').join(' · ') || '—';
  return (
    <li>
      <button
        onClick={onEdit}
        className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-muted/40"
      >
        <div className="min-w-0 flex-1">
          <div className="font-medium">{c.name}</div>
          <div className="truncate text-xs text-muted-foreground">{sub}</div>
        </div>
        <span className="text-xs tabular-nums text-muted-foreground">
          {new Date(c.createdAt).toLocaleDateString()}
        </span>
      </button>
    </li>
  );
}

function ClientForm({
  initial,
  isAdmin,
  onCancel,
  onSaved,
}: {
  initial: Client | null;
  isAdmin: boolean;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [contactPhone, setContactPhone] = useState(initial?.contactPhone ?? '');
  const [contactEmail, setContactEmail] = useState(initial?.contactEmail ?? '');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: name.trim(),
        contactPhone: isAdmin ? (contactPhone.trim() || undefined) : undefined,
        contactEmail: isAdmin ? (contactEmail.trim() || undefined) : undefined,
        notes: notes.trim() || undefined,
      };
      if (initial) {
        return api(`/api/clients/${initial.id}`, { method: 'PATCH', body });
      }
      return api('/api/clients', { method: 'POST', body });
    },
    onSuccess: onSaved,
    onError: (err) => setError(err instanceof ApiError ? `Save failed (${err.status})` : 'Save failed'),
  });

  return (
    <Card>
      <CardHeader className="flex-row items-center">
        <CardTitle className="text-base">
          {initial ? `Edit · ${initial.name}` : 'New client'}
        </CardTitle>
        <Button variant="ghost" size="icon" className="ml-auto" onClick={onCancel}>
          <X className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1">
          <Label htmlFor="cname">Name *</Label>
          <Input id="cname" value={name} onChange={(e) => setName(e.target.value)} placeholder="Kapoor Family" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label htmlFor="cphone">
              Phone {isAdmin ? '' : <span className="text-xs text-muted-foreground">(masked — admin-only)</span>}
            </Label>
            <Input
              id="cphone"
              value={isAdmin ? contactPhone : maskPhone(contactPhone)}
              onChange={(e) => setContactPhone(e.target.value)}
              placeholder="+91 98xxx"
              disabled={!isAdmin}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="cemail">
              Email {isAdmin ? '' : <span className="text-xs text-muted-foreground">(masked — admin-only)</span>}
            </Label>
            <Input
              id="cemail"
              value={isAdmin ? contactEmail : maskEmail(contactEmail)}
              onChange={(e) => setContactEmail(e.target.value)}
              placeholder="client@example.com"
              disabled={!isAdmin}
            />
          </div>
        </div>
        <div className="space-y-1">
          <Label htmlFor="cnotes">Notes</Label>
          <Input id="cnotes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything to remember" />
        </div>
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onCancel}>Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={!name.trim() || save.isPending}>
            {save.isPending ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : null}
            {initial ? 'Save changes' : 'Create client'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function SkeletonRows() {
  return (
    <Card>
      <CardContent className="space-y-2 p-4">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-12 animate-pulse rounded-md bg-muted" />
        ))}
      </CardContent>
    </Card>
  );
}
